import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Recuperação de senha do login do tenant (dono do salão) via código de 6
 * dígitos enviado no WhatsApp cadastrado do salão.
 *
 * 2 passos:
 *   1) e-mail  → POST /auth/forgot-password  → "verifique seu WhatsApp"
 *   2) código + nova senha → POST /auth/reset-password → volta pro login
 *
 * Renderiza via portal em document.body — a tela de login tem ancestrais com
 * backdrop-blur, que quebrariam um position:fixed aninhado.
 */
const ForgotPasswordModal = ({ open, initialEmail = "", onClose, onResetDone }) => {
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState(initialEmail);
    const [code, setCode] = useState("");
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0); // segundos até poder reenviar

    useEffect(() => {
        if (open) {
            setStep(1);
            setEmail(initialEmail || "");
            setCode(""); setPwd(""); setPwd2(""); setShowPwd(false);
            setLoading(false); setCooldown(0);
        }
    }, [open, initialEmail]);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    // trava o scroll do fundo enquanto aberto
    useEffect(() => {
        if (!open || typeof document === "undefined") return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open || typeof document === "undefined") return null;

    const requestCode = async (e) => {
        e?.preventDefault?.();
        const em = email.trim().toLowerCase();
        if (!em.includes("@")) { toast.error("Informe um e-mail válido."); return; }
        setLoading(true);
        try {
            const res = await api.post("/auth/forgot-password", { email: em });
            toast.success("Pronto!", { description: res?.message || "Se o e-mail existir, o código foi enviado no WhatsApp." });
            setStep(2);
            setCooldown(60);
        } catch (err) {
            toast.error("Não foi possível enviar o código", { description: err?.message || "Tente novamente." });
        } finally {
            setLoading(false);
        }
    };

    const submitReset = async (e) => {
        e?.preventDefault?.();
        const c = code.trim();
        if (c.length !== 6 || !/^\d{6}$/.test(c)) { toast.error("O código tem 6 dígitos."); return; }
        if (pwd.length < 6) { toast.error("A nova senha precisa ter ao menos 6 caracteres."); return; }
        if (pwd !== pwd2) { toast.error("As senhas não conferem."); return; }
        setLoading(true);
        try {
            const res = await api.post("/auth/reset-password", {
                email: email.trim().toLowerCase(), code: c, new_password: pwd,
            });
            toast.success("Senha atualizada!", { description: res?.message || "Entre com a nova senha." });
            onResetDone?.(email.trim().toLowerCase());
            onClose?.();
        } catch (err) {
            toast.error("Não foi possível trocar a senha", { description: err?.message || "Verifique o código e tente de novo." });
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "mt-2 w-full rounded-xl border border-white/10 bg-slate-950/60 px-4 py-3 text-white outline-none focus:border-amber-500/60 transition-colors min-h-[48px]";
    const labelCls = "text-[11px] uppercase tracking-[0.25em] text-slate-400";

    return createPortal(
        <div
            data-testid="forgot-password-modal"
            className="fixed inset-0 z-[2147483000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            style={{ position: "fixed", inset: 0 }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 shadow-2xl max-h-[92vh] overflow-y-auto"
            >
                <div className="absolute -inset-px rounded-3xl opacity-40 blur-2xl pointer-events-none bg-gradient-to-br from-amber-500 to-amber-900"></div>

                <header className="relative p-6 md:p-7 border-b border-white/5 flex items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400">
                            Recuperar acesso · Etapa {step}/2
                        </div>
                        <h2 className="salon-heading text-2xl text-white mt-1">
                            {step === 1 ? "Esqueci minha senha" : "Novo código, nova senha"}
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="forgot-close-btn"
                        onClick={onClose}
                        aria-label="Fechar"
                        className="shrink-0 h-10 w-10 min-h-[44px] min-w-[44px] rounded-full bg-white/5 hover:bg-white/10 text-white transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                {step === 1 ? (
                    <form onSubmit={requestCode} className="relative p-6 md:p-7 space-y-4">
                        <p className="text-sm text-slate-400 leading-relaxed">
                            Enviamos um código de 6 dígitos para o <strong className="text-slate-200">WhatsApp
                            cadastrado do salão</strong>. Informe o e-mail do login do dono.
                        </p>
                        <label className="block">
                            <span className={labelCls}>E-mail do login</span>
                            <input
                                data-testid="forgot-email"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className={inputCls}
                                autoFocus
                            />
                        </label>
                        <button
                            type="submit"
                            data-testid="forgot-request-btn"
                            disabled={loading}
                            className="w-full min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-slate-950 font-semibold uppercase tracking-[0.2em] text-sm transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60"
                        >
                            {loading
                                ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Enviando…</>
                                : <>Enviar código <i className="fa-brands fa-whatsapp ml-2"></i></>}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={submitReset} className="relative p-6 md:p-7 space-y-4">
                        <p className="text-sm text-slate-400 leading-relaxed">
                            Verifique o <strong className="text-slate-200">WhatsApp do salão</strong>. Digite o
                            código e escolha a nova senha.
                        </p>
                        <label className="block">
                            <span className={labelCls}>Código (6 dígitos)</span>
                            <input
                                data-testid="forgot-code"
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={6}
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                className={`${inputCls} tracking-[0.4em] font-mono text-center text-lg`}
                                autoFocus
                            />
                        </label>
                        <label className="block">
                            <span className={labelCls}>Nova senha</span>
                            <div className="relative mt-2">
                                <input
                                    data-testid="forgot-new-password"
                                    type={showPwd ? "text" : "password"}
                                    autoComplete="new-password"
                                    value={pwd}
                                    onChange={(e) => setPwd(e.target.value)}
                                    className="w-full rounded-xl border border-white/10 bg-slate-950/60 pl-4 pr-11 py-3 text-white outline-none focus:border-amber-500/60 transition-colors min-h-[48px]"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                                    className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                                >
                                    <i className={`fa-solid ${showPwd ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                                </button>
                            </div>
                        </label>
                        <label className="block">
                            <span className={labelCls}>Confirmar nova senha</span>
                            <input
                                data-testid="forgot-confirm-password"
                                type={showPwd ? "text" : "password"}
                                autoComplete="new-password"
                                value={pwd2}
                                onChange={(e) => setPwd2(e.target.value)}
                                className={inputCls}
                            />
                        </label>

                        <button
                            type="submit"
                            data-testid="forgot-reset-btn"
                            disabled={loading}
                            className="w-full min-h-[52px] rounded-xl bg-amber-500 hover:bg-amber-400 active:bg-amber-600 text-slate-950 font-semibold uppercase tracking-[0.2em] text-sm transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60"
                        >
                            {loading
                                ? <><i className="fa-solid fa-spinner fa-spin mr-2"></i>Salvando…</>
                                : <>Trocar senha <i className="fa-solid fa-check ml-2"></i></>}
                        </button>

                        <div className="flex items-center justify-between text-xs pt-1">
                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="text-slate-500 hover:text-slate-300 transition-colors"
                            >
                                ← Trocar e-mail
                            </button>
                            <button
                                type="button"
                                data-testid="forgot-resend-btn"
                                onClick={requestCode}
                                disabled={loading || cooldown > 0}
                                className="text-amber-400/90 hover:text-amber-300 transition-colors disabled:text-slate-600 disabled:cursor-not-allowed"
                            >
                                {cooldown > 0 ? `Reenviar em ${cooldown}s` : "Reenviar código"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default ForgotPasswordModal;
